// src/components/pushNudgeCard/PushNudgeCard.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PushSubscribeButton from '../pushSubscribeButton/PushSubscribeButton.jsx';

export default function PushNudgeCard({ jwt, vapidKey, product = 'connect' }) {
  const [state, setState] = useState({
    supported: true,
    permission: 'default', // 'default' | 'granted' | 'denied'
    subscribed: false,
    ready: false, // service worker ready
    reason: '', // diagnostic hint for disabled button
  });
  const [dismissed, setDismissed] = useState(false);

  // Persisted dismissal key per product (so Connect and Trailblazer can differ)
  const DISMISS_KEY = useMemo(
    () => `pushNudge:dismissed:${product}`,
    [product],
  );

  // Load persisted dismissal on mount
  useEffect(() => {
    const v = localStorage.getItem(DISMISS_KEY);
    if (v === '1') setDismissed(true);
  }, [DISMISS_KEY]);

  // Re-show card after 2 hours if dismissed
  useEffect(() => {
    let timer;
    if (dismissed) {
      localStorage.setItem(DISMISS_KEY, '1');
      timer = setTimeout(() => {
        setDismissed(false);
        localStorage.removeItem(DISMISS_KEY);
      }, 2 * 60 * 60 * 1000); // 2 hours
    }
    return () => clearTimeout(timer);
  }, [dismissed, DISMISS_KEY]);

  const onStateChange = useCallback((next) => {
    setState((prev) => ({ ...prev, ...next }));
  }, []);

  // --- Environment detection + reconciliation ---
  useEffect(() => {
    let cancelled = false;

    const detect = async () => {
      // Basic feature checks
      const hasSW = 'serviceWorker' in navigator;
      const hasPush = 'PushManager' in window;
      const hasNotif = 'Notification' in window;

      // iOS Safari nuance: push requires installed PWA (standalone)
      const isIOS = /iP(hone|od|ad)/.test(navigator.userAgent);
      const isStandalone =
        window.matchMedia?.('(display-mode: standalone)')?.matches ||
        window.navigator.standalone;
      const https = window.isSecureContext;

      let supported = hasSW && hasPush && hasNotif && https;
      let reason = '';
      if (!https) reason = 'Requires HTTPS';
      if (!hasNotif) reason = 'Notifications API not available';
      if (!hasPush) reason = 'PushManager not available';
      if (!hasSW) reason = 'Service Worker not available';
      if (isIOS && supported && !isStandalone) {
        // On iOS, PushManager exists only for installed PWAs
        supported = false;
        reason = 'Install app to enable push on iOS';
      }

      const permission = hasNotif ? Notification.permission : 'denied';
      if (!supported) {
        if (!cancelled)
          onStateChange({
            supported,
            permission,
            ready: false,
            subscribed: false,
            reason,
          });
        return;
      }

      try {
        const reg = await navigator.serviceWorker.ready;
        if (cancelled) return;
        const sub = await reg.pushManager.getSubscription();
        if (cancelled) return;

        onStateChange({
          supported,
          permission, // 'granted' | 'denied' | 'default'
          ready: true,
          subscribed: !!sub,
          reason: '',
        });
      } catch (e) {
        if (!cancelled)
          onStateChange({
            supported: false,
            permission,
            ready: false,
            subscribed: false,
            reason: e?.message || 'SW not ready',
          });
      }
    };

    detect();

    // Reconcile when user returns (e.g., after changing OS/site settings)
    const onVis = () => {
      if (document.visibilityState === 'visible') detect();
    };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [onStateChange]);

  // Guard: don’t show nudge when it can’t do anything useful
  const canAct =
    state.supported && state.permission !== 'denied' && state.ready;

  const shouldShow =
    !dismissed &&
    state.supported &&
    state.permission !== 'denied' &&
    !state.subscribed;

  const helpText = useMemo(() => {
    if (!state.supported)
      return state.reason || 'Push not supported on this device.';
    if (state.permission === 'denied')
      return 'Notifications are blocked in your browser/site settings.';
    if (!state.ready) return 'Setting up…';
    return product === 'trailblazer'
      ? 'Get clue drops, check-ins, and rewards as they happen.'
      : 'Get likes, comments, and messages in real time.';
  }, [state.supported, state.permission, state.ready, state.reason, product]);

  return shouldShow ? (
    <div
      style={{
        display: 'grid',
        gap: 14,
        padding: 20,
        border: '2px solid #facc15',
        borderRadius: 18,
        background: 'linear-gradient(90deg, #fef9c3 0%, #a7f3d0 100%)',
        boxShadow: '0 4px 24px rgba(60,60,120,0.10)',
        maxWidth: 360,
        margin: '18px auto',
        fontFamily: 'inherit',
        position: 'relative',
        animation: 'popIn 0.5s cubic-bezier(.23,1.01,.32,1) both',
      }}
    >
      <div
        style={{
          fontWeight: 900,
          fontSize: 18,
          letterSpacing: 0.5,
          color: '#b45309',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <span role="img" aria-label="bell">
          🔔
        </span>
        Enable instant notifications
      </div>

      <div
        style={{
          fontSize: 14,
          color: '#374151',
          fontWeight: 500,
          background: 'rgba(255,255,255,0.7)',
          borderRadius: 8,
          padding: '6px 10px',
          boxShadow: '0 1px 4px #6366f133',
          minHeight: 24,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {helpText}
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <PushSubscribeButton
          jwt={jwt}
          vapidKey={vapidKey}
          product={product}
          onStateChange={onStateChange}
          // If your button supports a disabled prop, uncomment:
          // disabled={!canAct}
        />
        <button
          type="button"
          onClick={() => setDismissed(true)}
          style={{
            padding: '8px 14px',
            borderRadius: 10,
            border: 'none',
            background: 'linear-gradient(90deg, #f3f4f6 60%, #facc15 100%)',
            cursor: 'pointer',
            fontWeight: 700,
            color: '#b45309',
            boxShadow: '0 2px 8px #facc1533',
          }}
        >
          Not now
        </button>
      </div>
      {/* DEBUG: remove after testing */}
      {/* <pre
        style={{
          margin: 0,
          padding: '6px 8px',
          fontSize: 12,
          color: '#111',
          background: '#fff',
          borderRadius: 8,
          border: '1px dashed #e5e7eb',
          overflowX: 'auto',
        }}
      >
        {JSON.stringify(state, null, 2)}
      </pre> */}

      {/* Fun sparkle */}
      <span
        style={{
          position: 'absolute',
          top: 10,
          right: 18,
          fontSize: 22,
          animation: 'sparkle 1.2s infinite alternate',
          pointerEvents: 'none',
        }}
      >
        ✨
      </span>

      <style>
        {`
          @keyframes popIn { 0% { transform: scale(0.8); opacity: 0; } 100% { transform: scale(1); opacity: 1; } }
          @keyframes sparkle { 0% { opacity: 0.5; transform: rotate(-10deg) scale(1); } 100% { opacity: 1; transform: rotate(10deg) scale(1.2); } }
        `}
      </style>
    </div>
  ) : null;
}
